import type { NotificationOps } from '@mimir/client-common/fileSync/notificationOps';
import { getMasksFolderPathToUpload, getMasksFolderZipFileName, zipMasksFolder, getMaskAttachmentDownloadUrl, getMasksFolderName } from '@mimir/client-common/premiere/masks';
import { getTempDirInUserDataWith } from '@mimir/client-common/upload/premiere';
import { type AdobePanelContext, type ElectronContext, isInPremiere } from '@mimir/common/context/applicationContext';
import { getBasenameWithoutExtension, getExtname } from '@mimir/common/platformAbstraction/pathComposition';
import type { Logger } from '@mimir/common/types/logger';
import { exceptionToString } from '@mimir/common/util/exception';
import { copyFile, copyFileSync, isDirAvailableLocally, isFileAvailableLocally } from '@mimir/common/util/fs';
import { ConsoleLogger } from '@mimir/common/util/logging';
import type { NleProject } from '@mimir/primitive/nle/project';
import { getElectronShell, getUserDataPath } from '@mimir/web/desktop/utils';
import { showSavePremiereProjectDialog } from '@mimir/web/dialogs/savePremiereProject';
import { getSelectedProjectDownloadPath } from '@mimir/web/store/storeMisc';
import moment from 'moment';
import { getSanitizedFileNameForScript, makeDir } from './fileHelpers';
import { setCurrentProjectMimirItemIdMetadata } from './saveProjectUtils';
import { makeDefaultMzipOps } from '@mimir/mzip/defaultMzipOps';
import { DefaultBackendOps } from '@mimir/client-common/util/defaultBackendOperations';
import { makeStaticUrlProvider } from '@mimir/common/io/urlProvider';
import type { DownloadProgress } from '@mimir/common/types/downloader';
import { avoidCORSConflict } from '../util/thumbnail';
import { createUuid } from '@mimir/common/util/createUuid';
import { addNotification, deleteNotification, updateNotification } from '../globals/notifications';
import { type IProgressNotification, NotificationStackItemType } from '../model/notificationTypes';

export const DEFAULT_PREMIERE_PROJECT_FOLDER = 'Adobe Premiere Projects';

export const getProjectItemIdSuffix = (itemId: string) => {
  return `_${itemId.slice(-8)}`;
};

// windows doesn't allow ':' or '/' in filenames, so we use
// YYYYMMDDHHmmss format as suffix instead of direct date
export const getProjectNameSuffix = (itemId: string, isProjectTemplate: boolean) => {
  const timestamp = moment(new Date()).format('YYYYMMDDHHmmss');
  if (isProjectTemplate) {
    return `_${timestamp}`;
  }
  return `${getProjectItemIdSuffix(itemId)}_${timestamp}`;
};

async function getTempProjectLocation(
  services: {
    applicationContext: AdobePanelContext | ElectronContext,
    logger: Logger,
  },
  userDataSystemPath: string,
): Promise<string> {
  return (await getTempDirInUserDataWith(services, userDataSystemPath)) + services.applicationContext.pathSep + DEFAULT_PREMIERE_PROJECT_FOLDER;
}

export const zipMasksFolderFromProject = async (
  services: {
    applicationContext: AdobePanelContext | ElectronContext,
    logger: Logger,
  },
  project: NleProject,
) => {
  const sourceObjectMasksDirectoryPath = await getMasksFolderPathToUpload({
    applicationContext: services.applicationContext,
    logger: services.logger,
    sourceProjectPath: project.path,
  });
  if (!sourceObjectMasksDirectoryPath) {
    return null;
  }
  const tempDir = await getTempDirInUserDataWith(services, getUserDataPath(services.applicationContext));
  const outputZipFilePath = `${tempDir}${services.applicationContext.pathSep}${getMasksFolderZipFileName(project.name)}`;
  const { masksZipFileSize } = await zipMasksFolder({
    nodeApi: services.applicationContext.nodeApi,
    sourceObjectMasksDirectoryPath,
    outputZipFilePath,
  });

  return {
    outputZipFilePath,
    masksZipFileSize,
  };
};

const handleMasksFolderDownload = async (
  services: {
    applicationContext: AdobePanelContext | ElectronContext,
    logger: Logger,
  },
  config: {
    downloadPath: string,
  },
  params: {
    itemId: string,
    projectOriginalFileName: string,
    diskProjectFileNameWithoutExt: string,
    diskProjectFileDirPath: string,
  },
) => {
  const { itemId, projectOriginalFileName, diskProjectFileNameWithoutExt, diskProjectFileDirPath } = params;
  const downloadNotification: IProgressNotification = {
    type: NotificationStackItemType.Progress,
    message: 'Downloading masks folder: ',
    cancelBtn: { tooltip: 'Cancel masks folder download', handler: () => {}},
    progress: 0,
    key: createUuid(),
    isComplete: false,
    isClosed: false,
  };

  try {
    const masksAttachmentFileName = getMasksFolderZipFileName(projectOriginalFileName);
    const backendOps = new DefaultBackendOps(services.applicationContext.backendClient);
    const masksAttachmentDownloadUrl = await getMaskAttachmentDownloadUrl(backendOps, itemId, masksAttachmentFileName);
    if (masksAttachmentDownloadUrl === null) {
      // No masks attachment, no need to proceed
      return;
    }

    downloadNotification.message  = `Downloading masks folder: ${masksAttachmentFileName}`;
    addNotification(downloadNotification);

    const downloadedMasksZipFilePath = `${config.downloadPath}${services.applicationContext.pathSep}${itemId}_attachment_${masksAttachmentFileName}`;
    if (!await isFileAvailableLocally(services.applicationContext.nodeApi, services.logger, downloadedMasksZipFilePath)) {
      const abortController = new AbortController();
      downloadNotification.cancelBtn = { tooltip: 'Cancel masks folder download', handler: () => { abortController.abort(); }};
      const downloader = services.applicationContext.downloader;
      const abortSignal = abortController.signal;
      await downloader.downloadFlatFile({
        urlProvider: makeStaticUrlProvider(avoidCORSConflict(masksAttachmentDownloadUrl)),
        outputFilename: downloadedMasksZipFilePath,
        abortSignal,
        progressHandler: (progress: DownloadProgress) => {
          if (progress.totalNumberOfBytes === 'undetermined') {
            return;
          }
          const percentage = 100 * progress.numberOfBytesDownloaded / progress.totalNumberOfBytes;
          downloadNotification.progress = percentage;
          updateNotification(downloadNotification);
        },
      });
    }

    // Extract
    downloadNotification.message = `Extracting masks folder ${masksAttachmentFileName}`;
    updateNotification(downloadNotification);

    const mzipOps = makeDefaultMzipOps();
    await mzipOps.extractZipToDirectory({
      nodeApi: services.applicationContext.nodeApi,
      sourceZipFilePath: downloadedMasksZipFilePath,
      outputDirectoryPath: `${diskProjectFileDirPath}${services.applicationContext.pathSep}${getMasksFolderName(diskProjectFileNameWithoutExt)}`,
    });

    deleteNotification(downloadNotification);
  } catch (error) {
    downloadNotification.message = `Failed: ${downloadNotification.message}: ${error}`;
    updateNotification(downloadNotification);
  }
}

export const openProjectFile = async (
  services: {
    applicationContext: AdobePanelContext | ElectronContext,
    notificationOps: NotificationOps,
    logger: Logger,
  },
  config: {
    downloadPath: string,
    userDataSystemPath: string,
  },
  request: {
    projectOriginalFileName: string,
    filePath: string,
    title: string,
    itemId: string,
    suppressWarning: boolean,
    isProjectTemplate: boolean,
  },
) => {
  const {
    projectOriginalFileName,
    filePath,
    title,
    itemId,
    suppressWarning,
    isProjectTemplate,
  } = request;

  try {
    const projectIdentifier = getProjectNameSuffix(itemId, isProjectTemplate);
    const pathSep = services.applicationContext.pathSep;
    const ext = getExtname(filePath, pathSep);
    const sanitizedTitle = getSanitizedFileNameForScript(services.applicationContext, title);
    const shortName = getBasenameWithoutExtension(sanitizedTitle, pathSep);
    const projectFileNameWithoutExt = `${shortName}${projectIdentifier}`;
    const projectName = `${projectFileNameWithoutExt}${ext}`;

    let targetBaseFolder = getSelectedProjectDownloadPath();

    if (!targetBaseFolder) {
      targetBaseFolder = await getTempProjectLocation(services, config.userDataSystemPath);
    }

    if (!(await isDirAvailableLocally(services.applicationContext.nodeApi, services.logger, targetBaseFolder))) {
      await makeDir(services, targetBaseFolder);
    }

    const targetProjectPath = targetBaseFolder + pathSep + projectName;

    // Download and extract the masks folder first before opening the project. Otherwise
    // Premiere complains about missing the masks information, and has no way to recover
    // later without manually locating the folder, or re-opening the project.
    await handleMasksFolderDownload(services, config, {
      itemId,
      projectOriginalFileName,
      diskProjectFileNameWithoutExt: projectFileNameWithoutExt,
      diskProjectFileDirPath: targetBaseFolder
    });

    if (isInPremiere(services.applicationContext)) {
      await openProjectInPremiere(services.applicationContext, filePath, targetProjectPath);

      // Store the Mimir item ID in the project's root bin metadata
      // This allows us to track which Mimir item this project was opened from
      await setCurrentProjectMimirItemIdMetadata(services.applicationContext, itemId);
    } else {
      await openProjectInElectron(services.applicationContext, filePath, targetProjectPath, suppressWarning ? undefined : (msg) => services.notificationOps.displayBriefSuccess(msg));
    }
  } catch (err: unknown) {
    const error = exceptionToString(err);
    services.logger.error(`Error in openProjectFile: ${exceptionToString(error)}`);
    services.notificationOps.displayBriefError(`Failed to open project file ${title}. ${error}`);
  }
};

const openProjectInPremiere = async (adobeContext: AdobePanelContext, sourcePath: string, targetPath: string) => {
  const isProjectAlreadySaved = await isFileAvailableLocally(adobeContext.nodeApi, new ConsoleLogger(), targetPath);

  // If not available in the target path, copy it there first
  if (!isProjectAlreadySaved) {
    await copyFile(adobeContext.nodeApi, sourcePath, targetPath);
  }

  // Open from target path
  await adobeContext.cepBridgeClient.call('openFileAsPremiereProject', targetPath);
};

const openProjectInElectron = async (applicationContext: ElectronContext, sourcePath: string, targetPath: string, notifyUserFunc?: (msg: string) => void) => {
  const shell = getElectronShell(applicationContext);
  if (shell) {
    const appName = targetPath.endsWith('.drp') ? 'DaVinci Resolve' : 'Premiere Pro';
    if (notifyUserFunc) {
      notifyUserFunc(`Opening project in ${appName}...`);
    }
    await copyFile(applicationContext.nodeApi, sourcePath, targetPath);
    shell.openPath(targetPath);
  }
};

export const uploadCurrentProjectToMimir = async (applicationContext: AdobePanelContext | ElectronContext, folderId?: string) => {
  const currentProject = await applicationContext.cepBridgeClient.call('getActiveProjectInfo');
  await saveProject(applicationContext, currentProject);
  showSavePremiereProjectDialog(currentProject, folderId);
};

export const saveProject = async (applicationContext: AdobePanelContext | ElectronContext, project: NleProject): Promise<void> => {
  await applicationContext.cepBridgeClient.call('saveProject', project.path);
};

export const copyProjectToMediaLocation = (applicationContext: AdobePanelContext | ElectronContext, projectPath: string, itemId: string, downloadPath: string) => {
  try {
    if (!downloadPath) {
      console.debug('copyProjectToMediaLocation: skipping since download path is not configured yet');
      return;
    }

    const pathWithItemId = downloadPath + applicationContext.pathSep + itemId + '.prproj';
    console.debug(`copying project ${projectPath} to final location ${pathWithItemId}`);
    copyFileSync(applicationContext.nodeApi, projectPath, pathWithItemId);
  } catch (err) {
    console.debug(`error copying project ${projectPath} to final location: ${err}`);
  }
};
